import { useEffect, useRef, useState } from "react";
import type { StateEnum } from "@lending/types";

// How often we poll while decisioning runs.
const POLL_INTERVAL_MS = 2_000;
// After this we soften the copy ("taking longer than usual") but KEEP polling —
// the backend can legitimately still be retrying decisioning for several minutes.
const SLOW_THRESHOLD_MS = 90_000;
// Hard stop. Aligned with the backend decisioning budget (Celery retries with
// backoff, capped by the decisioning-processing sweeper). Only after this do we
// stop polling and show the error state.
const POLL_TIMEOUT_MS = 300_000;

// States where decisioning is still running and we should keep polling. Kept in
// sync with the non-navigating cases in CreditPull's state switch — any other
// state means the application has advanced (e.g. offers_generated) or reached a
// terminal state, so polling stops and the page navigates onward.
const IN_PROGRESS_STATES = new Set<StateEnum>([
  "creating",
  "decisioning_queued",
  "decisioning_processing",
]);

export type CreditPullPhase = "polling" | "slow" | "resolved" | "timeout";

interface ApplicationLike {
  state?: StateEnum;
}

// The subset of a lending application query hook this hook depends on: it must
// accept a refetchInterval callback and return { data }.
type ApplicationQueryHook<TApp extends ApplicationLike> = (options: {
  refetchInterval: (application: TApp | undefined) => number | false;
}) => { data: TApp | undefined };

/**
 * Polls a lending application while decisioning runs and enforces a hard
 * timeout. Shared across the Personal Loan, Credit Card, and Auto Loan credit
 * pull screens — each passes its own application query hook. Derives a `phase`:
 * - `polling`: still within the normal window, show the loading copy.
 * - `slow`: past {@link SLOW_THRESHOLD_MS} but still polling, show reassurance copy.
 * - `timeout`: hit {@link POLL_TIMEOUT_MS} without resolving, stop polling and
 *   show the error copy. The backend is authoritative for the terminal state; a
 *   sweeper marks stale applications `decisioning_failed`.
 * - `resolved`: the application left an in-progress state; the page navigates on.
 */
function useCreditPullTimeout<TApp extends ApplicationLike>(
  useApplicationQuery: ApplicationQueryHook<TApp>
) {
  const [isSlow, setIsSlow] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  // Read by the refetchInterval callback, which react-query holds across
  // renders; a ref keeps it reading the live value without a stale closure.
  const hasTimedOutRef = useRef(false);

  useEffect(() => {
    const slowHandle = setTimeout(() => setIsSlow(true), SLOW_THRESHOLD_MS);
    const timeoutHandle = setTimeout(() => {
      hasTimedOutRef.current = true;
      setHasTimedOut(true);
    }, POLL_TIMEOUT_MS);
    return () => {
      clearTimeout(slowHandle);
      clearTimeout(timeoutHandle);
    };
  }, []);

  const { data: application } = useApplicationQuery({
    refetchInterval: (app) => {
      if (hasTimedOutRef.current) {
        return false;
      }
      const state = app?.state;
      if (state && !IN_PROGRESS_STATES.has(state)) {
        return false;
      }
      return POLL_INTERVAL_MS;
    },
  });

  const state = application?.state;
  const isInProgress = !state || IN_PROGRESS_STATES.has(state);

  let phase: CreditPullPhase;
  if (!isInProgress) {
    // Decisioning resolved — the page navigates away even if we also timed out.
    phase = "resolved";
  } else if (hasTimedOut) {
    phase = "timeout";
  } else if (isSlow) {
    phase = "slow";
  } else {
    phase = "polling";
  }

  return { application, phase };
}

export { useCreditPullTimeout };
